/**
 * Data Source Context
 * 
 * Manages toggle between Dexie (legacy/mock) and Supabase data sources
 * Allows parity checking between the two systems
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { createLogger } from '../lib/utils/logger';

const logger = createLogger('DataSourceContext');

export type DataSourceType = 'dexie' | 'supabase';

interface DataSourceContextType {
  dataSource: DataSourceType;
  toggleDataSource: () => void;
  setDataSource: (source: DataSourceType) => void;
}

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

interface DataSourceProviderProps {
  children: ReactNode;
}

export function DataSourceProvider({ children }: DataSourceProviderProps) {
  // Default to Dexie in dev, Supabase in prod
  const [dataSource, setDataSourceState] = useState<DataSourceType>(() => {
    const saved = localStorage.getItem('mars2-data-source');
    return (saved as DataSourceType) || (import.meta.env.DEV ? 'dexie' : 'supabase');
  });

  useEffect(() => {
    localStorage.setItem('mars2-data-source', dataSource);
    logger.info('Data source changed', { source: dataSource.toUpperCase() });
  }, [dataSource]);

  const toggleDataSource = useCallback(() => {
    setDataSourceState(prev => prev === 'dexie' ? 'supabase' : 'dexie');
  }, []);

  const setDataSource = useCallback((source: DataSourceType) => {
    setDataSourceState(source);
  }, []);

  const value = useMemo(() => ({
    dataSource,
    toggleDataSource,
    setDataSource,
  }), [dataSource, toggleDataSource, setDataSource]);

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
}

export function useDataSource() {
  const context = useContext(DataSourceContext);
  if (context === undefined) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
}


